import { useBetraegeSichtbar, verdeckt } from "../../../app/betraege";
import { Fragment, type ReactNode, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { BeispielEtikett } from "../../../components/Beispiel";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { KopfMehr, Money, PillView, SortTh, Toolbar, useAction, useSorted, SortWahl } from "../../../components/ui";
import { date, money as moneyFmt } from "../../../lib/format";
import { Ansichtswahl, ArchivDialog, KoopLeer, StatusDialog, geklappt } from "./bausteine";
import { setzeKooperationsStatus } from "./daten";
import { useHeute, useKooperationen } from "./hooks";
import {
  FILTERS, baueZeilen, creatorFilterOptionen, dealCode, markenFilterOptionen, dealRange, itemKindLabel, labelMap, monthText, passtFilter, passtSuche, platformLabel, prozent as prozentFmt, satzQuelle,
  splitKontext, statusDialogText, zaehleFilter, type KoopFilter, type KoopZeile, zwischenstand, statusKurz, statusPill } from "./model";
import type { KoopDaten } from "./typen";
import { currentLang, istCreatorKonto, t, ta } from "../../../lib/sprache";
import { VoreinstellungenEintrag } from "../einstellungen/Voreinstellungen";
import { NurSchreiben } from "../../../app/nurLesen";
import { Icon } from "../../../components/Icon";

/** Kooperationen als Liste (11_app_agentur/deals-liste.html), Umfang wie im bisherigen Portal (AD-039 … AD-044, AD-049, AD-068). */

type SortKey = "creator" | "kooperation" | "status" | "monate" | "frist" | "honorar" | "agentur" | "geaendert";

/** S2: Monat kurz für die kompakte Liste („Nov. 26“); der volle Name steht für Vorleser daneben */
function monatKurz(key: string): string {
  const d = new Date(`${key.slice(0, 7)}-01T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString(currentLang() === "en" ? "en-GB" : "de-DE", { month: "short", year: "2-digit", timeZone: "UTC" });
}

export function Kooperationen() {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const prozent = verdeckt(sicht, prozentFmt);
  const ws = useStaffWorkspace();
  const navigate = useNavigate();
  const q = useKooperationen();
  const today = useHeute();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "aktiv") as KoopFilter;
  const search = params.get("q") ?? "";
  const creator = params.get("creator") ?? "";
  const marke = params.get("marke") ?? "";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [statusFuer, setStatusFuer] = useState<KoopZeile | null>(null);
  const [archivFrage, setArchivFrage] = useState(false);
  const { busy, run } = useAction();
  const labelsAll = useMemo(() => labelMap(q.data?.labels ?? []), [q.data?.labels]);

  const zeilen = useMemo(() => (q.data ? baueZeilen(q.data, { today, timeZone: ws.org.timezone, selbst: !!ws.isCreator }) : []), [q.data, today, ws.org.timezone]);
  const vorauswahl = zeilen.filter((z) => (!creator || z.deal.talent_id === creator) && (!marke || z.deal.brand_id === marke) && passtSuche(z, search));
  const counts = zaehleFilter(vorauswahl);
  const shown = useMemo(() => vorauswahl
    .filter((z) => passtFilter(z, filter))
    .sort((a, b) => a.status.rank - b.status.rank || (a.frist?.date ?? "9999").localeCompare(b.frist?.date ?? "9999")),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [zeilen, filter, creator, marke, search]);
  const { sorted, sort, toggle } = useSorted<KoopZeile, SortKey>(shown, {
    creator: (z) => z.talentName,
    kooperation: (z) => `${z.brandName} ${z.deal.title}`,
    status: (z) => z.status.rank,
    monate: (z) => (z.monate.length ? z.monate.filter((m) => m.done).length / z.monate.length : null),
    frist: (z) => z.frist?.date ?? null,
    honorar: (z) => z.honorarCents,
    agentur: (z) => z.agenturCents,
    geaendert: (z) => z.deal.updated_at,
  });

  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Loading />;
  const d: KoopDaten = q.data;
  const prefix = d.settings?.invoice_number_prefix;
  const ausgewaehlt = sorted.filter((z) => auswahl.has(z.deal.id));
  const alleGewaehlt = sorted.length > 0 && ausgewaehlt.length === sorted.length;
  const toggleAll = (on: boolean) => setAuswahl(on ? new Set(sorted.map((z) => z.deal.id)) : new Set());
  const toggleOne = (id: string, on: boolean) => setAuswahl((s) => {
    const n = new Set(s);
    if (on) n.add(id);
    else n.delete(id);
    return n;
  });
  const toggleOpen = (id: string) => setOffen((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  const imArchiv = filter === "archiv";
  const gefiltert = !!(search || creator || marke);
  const finanzen = ws.can("view_financials");


  return (
    <main id="main" className="content tk-koop">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          {/* Creator-Konto: die Navigation sagt „Deals“ (studioNav), die Seite auch */}
          <h1>{ta("Kooperationen", "Deals", "Deals", "Deals")}</h1>
          <p>{ta("Jede Kooperation mit Creator, Marke, Honorar und Monaten; daraus entstehen Vertrag und Rechnung.", "Every collaboration with creator, brand, fee and months; contract and invoice follow from it.", "Deine Deals mit Marken. Details mit einem Klick.", "Your deals with brands. Details with one click.")}</p>
        </div>
        <Ansichtswahl aktuell="liste">
          {/* D-2: eine Hauptaktion, Seltenes hinter „Mehr“ */}
          {/* Nur-Lesen (Test-Chat 04.11.2026): Anlegen entfällt ausdrücklich, nicht nur über die Regel in portal.css */}
          <NurSchreiben><Link className="btn primary" to="/agentur/kooperationen/neu" data-coco="kooperation-anlegen">{ta("Neue Kooperation", "New deal", "Neuer Deal", "New deal")}</Link></NurSchreiben>
          <KopfMehr>
            <NurSchreiben><Link to="/agentur/kooperationen/import">{t("Bestand übernehmen", "Take over existing data")}</Link></NurSchreiben>
            <VoreinstellungenEintrag modul="kooperationen" />
          </KopfMehr>
        </Ansichtswahl>
      </div>

      {zeilen.length === 0 ? (
        <KoopLeer talents={d.talents} />
      ) : (
        <>
          <Toolbar
            search={search}
            onSearch={(v) => setParam("q", v)}
            label={t("Liste durchsuchen", "Search list")}
            placeholder={t("Suchen …", "Search …")}
            filters={FILTERS.map((f) => ({ ...f, count: counts[f.key] }))}
            filter={filter}
            onFilter={(f) => {
              setAuswahl(new Set());
              setParam("filter", f === "aktiv" ? "" : f);
            }}
          >
            <select hidden={ws.isCreator} aria-label={t("Nach Creator filtern", "Filter by creator")} value={creator} onChange={(e) => setParam("creator", e.target.value)}>
              <option value="">{t("Alle Creator", "All creators")}</option>
              {creatorFilterOptionen(d.talents, zeilen, creator).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <select aria-label={t("Nach Marke filtern", "Filter by brand")} value={marke} onChange={(e) => setParam("marke", e.target.value)}>
              <option value="">{t("Alle Marken", "All brands")}</option>
              {markenFilterOptionen(d.brands, zeilen, marke).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Toolbar>

          {ausgewaehlt.length > 0 && (
            <div className="row" role="status">
              <strong>{ausgewaehlt.length === 1 ? t("1 Kooperation ausgewählt", "1 deal selected") : t(`${ausgewaehlt.length} Kooperationen ausgewählt`, `${ausgewaehlt.length} deals selected`)}</strong>
              <button onClick={() => setArchivFrage(true)}>{imArchiv ? t("Auswahl wiederherstellen", "Restore selection") : t("Auswahl archivieren", "Archive selection")}</button>
              <button onClick={() => setAuswahl(new Set())}>{t("Auswahl aufheben", "Clear selection")}</button>
            </div>
          )}

          <SortWahl />
          <div className="tablewrap tk-koop-karten tk-koop-liste tk-sticky-end">
            <table>
              <caption className="sr-only">{t("Kooperationen", "Deals")}</caption>
              <thead>
                <tr>
                  <th scope="col"><input type="checkbox" aria-label={t("Alle Zeilen auswählen", "Select all rows")} checked={alleGewaehlt} onChange={(e) => toggleAll(e.target.checked)} /></th>
                  {/* Creator-Konto (0178): erste Spalte ist die Marke, kein Agenturanteil */}
                  <SortTh label={ws.isCreator ? t("Marke", "Brand") : "Creator"} k={ws.isCreator ? "kooperation" : "creator"} sort={sort} toggle={toggle} />
                  <SortTh label={t("Kooperation", "Deal")} k="kooperation" sort={sort} toggle={toggle} />
                  <SortTh label="Status" k="status" sort={sort} toggle={toggle} />
                  {sicht && <SortTh label={t("Honorar netto", "Net fee")} k="honorar" sort={sort} toggle={toggle} money />}
                  {/* weiches Trennzeichen: das lange Wort darf im Spaltenkopf umbrechen, damit die Liste bei 1280 px passt */}
                  {sicht && !ws.isCreator && <SortTh label={t("Agentur\u00adeinnahmen", "Agency income")} k="agentur" sort={sort} toggle={toggle} money className="tk-koop-c-anteil" />}
                  <SortTh label={t("Monate", "Months")} k="monate" sort={sort} toggle={toggle} className="tk-koop-c-monate" />
                  <SortTh label={t("Nächste Frist", "Next deadline")} k="frist" sort={sort} toggle={toggle} className="tk-koop-c-frist" />
                  <th scope="col"><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((z) => {
                  const isOpen = offen.has(z.deal.id);
                  const done = z.monate.filter((m) => m.done).length;
                  const stage = z.arbeitsstandLabel;
                  return (
                    <Fragment key={z.deal.id}>
                    <tr className="tk-koop-zeile" onClick={(e) => {
                      // S2 (D-1): die ganze Zeile öffnet die Kooperation; Knöpfe, Links und Häkchen behalten ihre eigene Wirkung
                      if ((e.target as HTMLElement).closest("a, button, input, label, select")) return;
                      navigate(`/agentur/kooperationen/${z.deal.id}`);
                    }}>
                      <td className="tk-koop-auswahl"><input type="checkbox" aria-label={t(`${z.talentName} · ${z.deal.title} auswählen`, `Select ${z.talentName} · ${z.deal.title}`)} checked={auswahl.has(z.deal.id)} onChange={(e) => toggleOne(z.deal.id, e.target.checked)} /></td>
                      <td className="tk-koop-kopf"><Link to={`/agentur/kooperationen/${z.deal.id}`}><strong>{ws.isCreator ? z.brandName : z.talentName}</strong></Link><BeispielEtikett von={z.deal} /></td>
                      <td data-label={t("Kooperation", "Deal")} className="tk-koop-zelle">
                        {/* K-565 b: Zeile 1 der Titel, Zeile 2 Marke · Modell · Code (der Titel beginnt oft mit dem Markennamen) */}
                        <span className="tk-koop-titel" title={ws.isCreator ? z.deal.title : `${z.brandName} · ${z.deal.title}`}>{z.deal.title}</span>
                        <small>{ws.isCreator ? "" : `${z.brandName} · `}{z.modell} · {dealCode(z.deal, prefix)}{z.kampagne ? t(` · Kampagne ${z.kampagne.title}`, ` · campaign ${z.kampagne.title}`) : ""}{z.deal.exclusive ? t(" · exklusiv", " · exclusive") : ""}</small>
                      </td>
                      <td data-label="Status" className="tk-koop-status">
                        {/* D2-1: Pille einzeilig; in einer schmalen Liste die Kurzform, der volle Text im Tooltip */}
                        <span className="tk-status-lang"><PillView pill={z.status.pill} /></span>
                        {statusKurz(z.status.pill.label) && <span className="tk-status-kurz" title={z.status.pill.label}><PillView pill={{ ...z.status.pill, label: statusKurz(z.status.pill.label)! }} /></span>}
                        {z.status.hinweis && <small>{z.status.hinweis}</small>}
                        {/* D2-1: eingeklappte Spalte „Nächste Frist“ (schmale Liste) */}
                        {z.frist && <small className={`tk-koop-alt${z.frist.ueberfaellig ? " tk-koop-ueberfaellig" : ""}`}>{t("Frist ", "Due ")}{date(z.frist.date)} · {z.frist.art}{z.frist.ueberfaellig ? t(" · überfällig", " · overdue") : ""}</small>}
                        {/* K-526/K-559 f: der eigene Zwischenstand gehört zum Arbeitsstand („Arbeitsstand: In Produktion · Ware beim Creator seit …“) */}
                        {(() => {
                          const zs = q.data ? zwischenstand(z.deal, q.data.substeps) : null;
                          if (stage !== z.status.pill.label && !z.archiviert) return <small>{t("Arbeitsstand: ", "Working status: ")}{stage}{zs ? ` · ${zs.text}` : ""}</small>;
                          return zs ? <small className="tk-zwischenstand">{zs.text}</small> : null;
                        })()}
                      </td>
                      {sicht && <td data-label={t("Honorar netto", "Net fee")}>
                        {z.honorarCents == null ? <span className="muted">–</span> : <Money cents={z.honorarCents} currency={z.deal.currency} />}
                        {z.deal.model === "commission_only" && <small>{t("Reine Provision", "Commission only")}</small>}
                        {z.honorarCents != null && z.monate.length > 1 && <small>{z.monate.length} {t("Monate", "Months")}</small>}
                        {/* D2-1: eingeklappte Spalte „Agenturanteil“ (schmale Liste) */}
                        {!ws.isCreator && z.agenturCents != null && <small className="tk-koop-alt">{t("Anteil ", "Share ")}{money(z.agenturCents, z.deal.currency)}</small>}
                      </td>}
                      {sicht && !ws.isCreator && <td data-label={t("Agentur-Einnahmen", "Agency income")} className="tk-koop-c-anteil">
                        {z.agenturCents == null ? <span className="muted">–</span> : <Money cents={z.agenturCents} currency={z.deal.currency} />}
                        {/* N-3: höchstens zwei Zeilen („nach Vermittler“ hält zusammen, der Betrag bricht nur als Ganzes in die zweite Zeile) */}
                        {finanzen && z.vermittlerCents > 0 && z.agenturCents != null && <small className="tk-koop-vermittler" title={t(`Agentur-Einnahmen nach Abzug der Vermittlerprovision: ${money(z.agenturCents - z.vermittlerCents, z.deal.currency)}`, `Agency income after the referrer commission: ${money(z.agenturCents - z.vermittlerCents, z.deal.currency)}`)}>{t("nach\u00a0Vermittler", "after\u00a0referrer")} <span className="num">{money(z.agenturCents - z.vermittlerCents, z.deal.currency)}</span></small>}
                      </td>}
                      <td data-label={t("Monate", "Months")} className="tk-koop-monate">
                        {z.monate.length > 1 ? (
                          <>
                            <span className="tk-koop-monate-lang">{done} {t("von ", "of ")}{z.monate.length} {t("erledigt", "done")}</span><span className="tk-koop-monate-kurz" aria-hidden="true">{done}/{z.monate.length}</span><span className="progress tk-koop-fortschritt" role="img" aria-label={t(`${done} von ${z.monate.length} Monaten erledigt`, `${done} of ${z.monate.length} months done`)}>
                              <span style={{ width: `${Math.round((done / z.monate.length) * 100)}%` }} />
                            </span>
                          </>
                        ) : z.monate[0] ? <><span className="tk-koop-monate-lang">{monthText(z.monate[0].month)}</span><span className="tk-koop-monate-kurz" aria-hidden="true">{monatKurz(z.monate[0].month)}</span></> : "–"}
                      </td>
                      <td data-label={t("Nächste Frist", "Next deadline")} className="tk-koop-c-frist">
                        {z.frist ? date(z.frist.date) : "–"}
                        {z.frist && (() => {
                          // Kurzform „Marke überfällig“ statt dreizeiligem „Zahlung der Marke · überfällig“, der volle Text im Tooltip
                          const markeUeberfaellig = z.frist.ueberfaellig && z.frist.art === t("Zahlung der Marke", "Brand's payment");
                          return markeUeberfaellig
                            ? <small className="tk-koop-ueberfaellig" title={t("Zahlung der Marke überfällig", "Brand's payment overdue")}>{t("Marke überfällig", "Brand overdue")}</small>
                            : <small className={z.frist.ueberfaellig ? "tk-koop-ueberfaellig" : undefined}>{z.frist.art}{z.frist.ueberfaellig ? t(" · überfällig", " · overdue") : ""}</small>;
                        })()}
                      </td>
                      <td className="tk-koop-aktion"><button aria-expanded={isOpen} aria-label={t(`Details zu ${z.brandName} · ${z.deal.title} ${isOpen ? "ausblenden" : "anzeigen"}`, `${isOpen ? "Hide" : "Show"} details on ${z.brandName} · ${z.deal.title}`)} onClick={() => toggleOpen(z.deal.id)}><span className="tk-koop-auf-text">{isOpen ? t("Weniger", "Less") : "Details"}</span><span className={isOpen ? "tk-koop-pfeil tk-koop-pfeil-auf" : "tk-koop-pfeil"} aria-hidden="true"><Icon name="chevron" /></span></button></td>
                    </tr>
                    {isOpen && (
                      <tr className="tk-koop-detailzeile">
                        <td colSpan={sicht ? (ws.isCreator ? 8 : 9) : 7}><ZeilenDetails z={z} d={d} labels={labelsAll} onStatus={() => setStatusFuer(z)} timeZone={ws.org.timezone} /></td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      {gefiltert || filter !== "aktiv" ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.") : t("In dieser Ansicht gibt es keine Kooperationen.", "There are no deals in this view.")}
                      {(gefiltert || filter !== "aktiv") && <> <button onClick={() => setParams(new URLSearchParams(), { replace: true })}>{t("Filter zurücksetzen", "Reset filters")}</button></>}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="source-note">
            {sorted.length === 1 ? t("1 Kooperation", "1 deal") : t(`${sorted.length} Kooperationen`, `${sorted.length} deals`)} {ta("· „Aktiv“ blendet Erledigtes aus; monatliche Kooperationen mit offenen Monaten bleiben sichtbar. Status aus Rechnungen und Anforderungen, sonst Arbeitsstand der Agentur. Agenturanteil = Anteil plus Agenturgebühr, netto.", "· “Active” hides what is done; monthly deals with open months stay visible. Status from invoices and requests, otherwise the agency's working status. Agency share = share plus agency fee, net.", "· „Aktiv“ blendet Erledigtes aus; monatliche Kooperationen mit offenen Monaten bleiben sichtbar. Status aus Rechnungen, sonst dein Arbeitsstand.", "· “Active” hides what is done; monthly deals with open months stay visible. Status from invoices, otherwise your working status.")}</p>
        </>
      )}

      {statusFuer && (
        <StatusDialog
          open={!!statusFuer}
          onClose={() => setStatusFuer(null)}
          titel={istCreatorKonto() ? t(`Status · ${statusFuer.brandName}`, `Status · ${statusFuer.brandName}`) : t(`Status · ${statusFuer.brandName} × ${statusFuer.talentName}`, `Status · ${statusFuer.brandName} × ${statusFuer.talentName}`)}
          text={statusDialogText(d.settings?.revenue_start)}
          aktuell={statusFuer.deal.status}
          labels={d.labels}
          busy={busy !== null}
          onSave={(s) => run("status", () => geklappt(setzeKooperationsStatus(api, statusFuer.deal, statusFuer.monate.map((m) => m.period), s)), t("Status gespeichert.", "Status saved."))}
        />
      )}
      <ArchivDialog
        open={archivFrage}
        onClose={() => setArchivFrage(false)}
        archiv={!imArchiv}
        deals={ausgewaehlt.map((z) => ({ id: z.deal.id, label: istCreatorKonto() ? `${z.brandName} · ${z.deal.title}` : `${z.brandName} × ${z.talentName} · ${z.deal.title}` }))}
        onDone={() => setAuswahl(new Set())}
      />
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}

/** Aufgeklappte Zeile (AD-042): Kooperation, Monate, Budget und Konditionen, Marke und Rechnung, Notizen. */
function ZeilenDetails({ z, d, labels, onStatus, timeZone }: { z: KoopZeile; d: KoopDaten; labels: ReadonlyMap<string, string>; onStatus: () => void; timeZone: string }) {
  const sicht = useBetraegeSichtbar();
  const money = verdeckt(sicht, moneyFmt);
  const prozent = verdeckt(sicht, prozentFmt);
  const deal = z.deal;
  const kontext = splitKontext(deal, z.terms, z.talent, d.settings);
  const brand = z.brand as (typeof z.brand & { invoice_email?: string | null }) | undefined;
  // Stripe-Aufbau (Inhaber 28.10.2026): Bezeichnung über dem Wert, linksbündig; Beträge als Aufstellung mit Wert rechts
  const line = (label: string, value: ReactNode | null | undefined) => value ? (
    <div className="tk-fakt"><dt>{label}</dt><dd>{value}</dd></div>
  ) : null;
  const posten = (label: string, value: string | null | undefined) => value ? (
    <div className="tk-posten"><dt>{label}</dt><dd>{value}</dd></div>
  ) : null;
  const fee = kontext.fee ? (kontext.fee.type === "percent" ? t(`${prozent(kontext.fee.percent)} je Monat`, `${prozent(kontext.fee.percent)} per month`) : t(`${money(kontext.fee.amountCents, deal.currency)} einmalig`, `${money(kontext.fee.amountCents, deal.currency)} one-off`)) : t("Keine", "None");
  const address = [deal.billing_street, [deal.billing_postal_code, deal.billing_city].filter(Boolean).join(" "), deal.billing_country].filter(Boolean).join(", ");
  return (
    <div className="tk-koop-auf">
      <section className="tk-koop-auf-teil">
        <h3>{t("Kooperation", "Deal")}</h3>
        <dl className="tk-fakten">
          {line(t("Zeitraum", "Period"), dealRange(deal, z.monate.map((m) => m.period)))}
          {line(t("Plattformen", "Platforms"), (deal.platforms ?? []).map((p) => platformLabel(p, d.platforms)).join(", "))}
          {line(t("Umfang", "Scope"), deal.deliverables)}
          {line(t("Nutzungsrechte", "Usage rights"), [deal.usage_rights, deal.usage_rights_until ? t(`bis ${date(deal.usage_rights_until)}`, `until ${date(deal.usage_rights_until)}`) : null].filter(Boolean).join(" · "))}
          {line(t("Content-Deadline", "Content deadline"), deal.content_deadline ? date(deal.content_deadline) : null)}
          {line(t("Exklusivität", "Exclusivity"), deal.exclusive ? [deal.exclusive_scope, deal.exclusive_until ? t(`bis ${date(deal.exclusive_until)}`, `until ${date(deal.exclusive_until)}`) : null].filter(Boolean).join(" · ") || t("Ja", "Yes") : null)}
          {z.kampagne && line(t("Kampagne", "Campaign"), <Link to={`/agentur/kooperationen/kampagne/${z.kampagne.id}`}>{z.kampagne.title}</Link>)}
          {deal.link && line(t("Link", "Link"), <a href={deal.link} target="_blank" rel="noreferrer noopener">{t("Link öffnen ↗", "Open link ↗")}</a>)}
          {/* Studio (Creator-Konto): kein Creator-Portal, keine Provision, keine Agenturgebühr, keine Notiz an einen Creator */}
          {!istCreatorKonto() && line(t("Im Creator-Portal", "In the creator portal"), deal.visible_to_talent ? t("sichtbar", "visible") : t("nicht sichtbar", "not visible"))}
          {line(t("Zuletzt geändert", "Last changed"), date(deal.updated_at, timeZone))}
        </dl>
      </section>
      <section className="tk-koop-auf-teil">
        <h3>{t("Budget & Konditionen", "Budget & terms")}</h3>
        <dl className="tk-aufstellung">
          {/* Je Monat: Monat mit Stand (früher die Monatspillen) und Honorar rechts */}
          {z.monate.map((m) => {
            const p = statusPill(m.status, labels);
            return (
              <div key={m.period.id} className="tk-posten">
                <dt><span>{monthText(m.month)}</span><PillView pill={p} /></dt>
                <dd>{m.period.amount_cents != null ? money(m.period.amount_cents, deal.currency) : "—"}</dd>
              </div>
            );
          })}
          {z.items.map((i) => <Fragment key={i.id}>{posten(`+ ${itemKindLabel(i.kind, d.itemKinds)} · ${i.description}${i.commissionable ? "" : t(" (provisionsfrei)", " (commission-free)")}`, money(i.amount_cents, deal.currency))}</Fragment>)}
        </dl>
        <dl className="tk-fakten">
          {!istCreatorKonto() && line(t("Provisionssatz", "Commission rate"), kontext.rate ? `${prozent(kontext.rate.percent)} · ${satzQuelle(deal, kontext.rate)}` : kontext.fehler)}
          {!istCreatorKonto() && line(t("Agenturgebühr", "Agency fee"), `${fee}${kontext.feeQuelle === "agentur" ? t(" · Standard der Agentur", " · agency default") : ""}`)}
          {line(t("Pauschal versteuerte Produkte", "Products taxed at a flat rate"), deal.products_flat_taxed ? t("Ja", "Yes") : t("Nein", "No"))}
        </dl>
      </section>
      <section className="tk-koop-auf-teil">
        <h3>{t("Marke / Rechnung", "Brand / invoice")}</h3>
        <dl className="tk-fakten">
          {line(t("Firma (Rechnung)", "Company (invoice)"), deal.billing_legal_name ?? z.brand?.legal_name ?? z.brandName)}
          {line(t("E-Mail für Rechnungen", "Email for invoices"), deal.billing_email ?? brand?.invoice_email ?? null)}
          {line(t("Rechnungsadresse", "Billing address"), address || null)}
          {line(t("USt-IdNr.", "VAT ID"), deal.billing_vat_id)}
          {line(t("Zahlungsziel", "Payment term"), deal.billing_payment_terms_days != null ? t(`${deal.billing_payment_terms_days} Tage`, `${deal.billing_payment_terms_days} days`) : null)}
          {line(t("Referenz / Bestellnummer", "Reference / order number"), deal.billing_reference)}
        </dl>
      </section>
      <section className="tk-koop-auf-teil">
        <h3>{t("Notizen", "Notes")}</h3>
        <dl className="tk-fakten">
          {!istCreatorKonto() && line(t("Für den Creator", "For the creator"), deal.talent_note || <span className="muted">{t("Keine Notiz", "No note")}</span>)}
          {line(t("Intern", "Internal"), z.terms?.internal_note || <span className="muted">{t("Keine Notiz", "No note")}</span>)}
        </dl>
      </section>
      <div className="tk-koop-auf-aktionen">
        {!z.archiviert && <button onClick={onStatus}>{t("Status ändern", "Change status")}</button>}
        <Link className="btn" to={`/agentur/kooperationen/${deal.id}/bearbeiten`}>{t("Bearbeiten", "Edit")}</Link>
        <Link className="btn primary" to={`/agentur/kooperationen/${deal.id}`}>{t("Öffnen", "Open")}</Link>
      </div>
    </div>
  );
}
